<template>
  <div class="main-page-container">
    <PageHeader
      className="d-flex align-items-center "
      :title="$t('trackAndTrace.title')"
    />

    <!-- grid -->
    <section
      class="emergency-list-table track-trace-white-bg-sec"
      :class="!showTable ? 'white-section-height' : ''"
      style="background-color: white"
    >
      <div class="filters-wrapper">
        <div class="filters d-flex align-items-center">
          <div class="filter user-filter mr-4">
            <VpMultiselect
              :showPlaceholderIcon="true"
              :width="'361'"
              :isLoading="usersLoading"
              :showOptionsBorder="true"
              :showNoResults="true"
              v-model="user"
              label="fullName"
              :options="usersOptions"
              :allowEmpty="true"
              :show-labels="true"
              zIndex="999"
              :title="$t('common.user')"
              :disabled="!usersOptions.length"
              :backgroundColor="'#f2f2f2'"
              :noResultMessage="'No results found'"
            />
          </div>
          <div class="filter date-filte mr-4 ml-4"> 
            <date-picker
              v-model="date"
              append-to-body
              value-type="YYYY-MM-DD"
              format="YYYY-MM-DD"
              :placeholder="$t('common.date')"
              class="pre-reg-datepicker"
              :clearable="true"
              range
              @input="changeDateFilter"
            >
              <i slot="icon-calendar">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.666656 14.8332C0.666656 16.2498 1.74999 17.3332 3.16666 17.3332H14.8333C16.25 17.3332 17.3333 16.2498 17.3333 14.8332V8.1665H0.666656V14.8332ZM14.8333 2.33317H13.1667V1.49984C13.1667 0.999837 12.8333 0.666504 12.3333 0.666504C11.8333 0.666504 11.5 0.999837 11.5 1.49984V2.33317H6.49999V1.49984C6.49999 0.999837 6.16666 0.666504 5.66666 0.666504C5.16666 0.666504 4.83332 0.999837 4.83332 1.49984V2.33317H3.16666C1.74999 2.33317 0.666656 3.4165 0.666656 4.83317V6.49984H17.3333V4.83317C17.3333 3.4165 16.25 2.33317 14.8333 2.33317Z"
                    :fill="date ? '#103F5E' : '#787878'"
                  />
                </svg>
              </i>
            </date-picker>
          </div>

          <b-button
            :disabled="!user || !date || isFilter"
            pill
            class="d-flex align-items-center justify-content-center text-center filter-btn cursor-pointer"
            variant="primary"
            size="sm"
            @click="onFilterSubmit"
          >
            {{ $t("common.filter") }}
            <i
              v-if="isFilter"
              style="margin-inline-start: 5px"
              class="bx bx-loader bx-spin font-size-16 align-middle rounded"
            ></i>
          </b-button>
        </div>
      </div>
      <div class="emptyspace-ubder-btn">
        <div class="sep-btn-line"></div>
      </div>

      <div v-if="!usersData.length && !showTable">
        <h6 class="track-trace-hint-grid">
          {{ $t("trackAndTrace.selectUserDateRangeMessage") }}
        </h6>
      </div>

      <DxDataGrid
        v-else
        id="trackAndTraceGridContainer"
        :data-source="rowsData"
        :ref="dataGridRefName"
        :remote-operations="false"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="true"
        :showRowLines="true"
        :repaint-changes-only="true"
        :two-way-binding-enabled="true"
        :no-data-text="$t('common.grid.noDataYet')"
        @content-ready="contentReady($event)"
        @editor-preparing="onEditorPreparing($event)"
        @data-error-occurred="onDataErrorOccurred($event)"
        :rtl-enabled="rtlEnabled"
      >
        <DxScrolling mode="standard" :scroll-by-content="rtlEnabled" :use-native="!rtlEnabled" />

        <DxStateStoring
          :enabled="true"
          :customLoad="customLoad"
          :customSave="customSave"
          type="custom"
        />

        <DxFilterRow :visible="true" />

        <DxHeaderFilter :visible="true" />

        <DxColumnChooser
          :enabled="true"
          mode="select"
          :title="$t('common.grid.columnChooser')"
        />

        <DxPager
          :visible="true"
          :allowed-page-sizes="[5, 10, 25, 50, 100]"
          :display-mode="'full'"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
          :infoText="rtlEnabled ?  'صفحة {0} من {1} ({2} إجمالي العناصر)' : ''"
        />

        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem location="before" template="exportButtonTemplate" />
          <DxItem location="before" template="clearAllTemplate" />
        </DxToolbar>

        <DxColumn
          data-field="firstName"
          :caption="$t('common.firstName')"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
          :width="200"
        />

        <DxColumn
          data-field="lastName"
          :caption="$t('common.lastName')"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
          :width="200"
        />

        <DxColumn
          data-field="userType"
          :caption="$t('common.userType')"
          :visible="true"
          :filter-operations="allowedOperations"
          :allow-filtering="false"
          :allow-header-filtering="true"
          :width="200"
        />

        <DxColumn
          data-field="inputType"
          :caption="$t('common.inputType')"
          css-class="whiteSpaceColumn"
          :allow-filtering="true"
          :allow-header-filtering="false"
          :visible="true"
          :filter-operations="allowedOperations"
          :width="200"
        />

        <DxColumn
          data-field="device"
          :caption="$t('common.device')"
          css-class="whiteSpaceColumn"
          :allow-filtering="true"
          :allow-header-filtering="false"
          :visible="true"
          :filter-operations="allowedOperations"
          :width="200"
        />

        <DxColumn
          data-field="sites"
          :caption="$t('common.site')"
          css-class="whiteSpaceColumn"
          :visible="true"
          :allow-filtering="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :width="200"
        />

        <DxColumn
          data-field="areas"
          :caption="$t('common.area')"
          css-class="whiteSpaceColumn"
          :visible="true"
          :allow-filtering="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :width="200"
        />

        <DxColumn
          data-field="date"
          :caption="$t('common.date')"
          css-class="whiteSpaceColumn"
          :visible="true"
          :allow-filtering="false"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :width="200"
        />

        <template #exportButtonTemplate>
          <DxDropDownButton
            class="export-dropdown"
            :items="buttonData"
            :text="$t('common.grid.export')"
            @item-click="onItemClick"
          />
        </template>

        <template #columnButtonTemplate>
          <button class="custom-columns" @click="dataGrid.showColumnChooser()">
            <span class="custom-text">{{ $t("common.grid.columns") }} </span>
            <span class="custom-icon">
              <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
            </span>
          </button>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols d-flex align-items-center">
            <div class="toolbar-custom-line"></div>
            <Button
              :text="$t('common.grid.clearFilters')"
              @click="reset()"
              styling-mode="outlined"
              type="danger"
              class="clear-CustomColums"
            />
          </div>
        </template>
      </DxDataGrid>
    </section>
  </div>
</template>

<script>
const dataGridRefName = "dataGridEmergencyList";
import { mapActions, mapGetters } from "vuex";
import PageHeader from "../../../components/page-header.vue";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import { tracAndTraceColumns } from "@/utils/dataSource";
import "jspdf-autotable";
import { default as Button } from "devextreme-vue/button";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import { jsPDF } from "jspdf";
import saveAs from "file-saver";
import reportsService from "@/services/reports";
import amiriFont from "@/assets/fonts/Amiri-Regular.ttf"; // Load the Cairo font

import DxDataGrid, {
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxColumnChooser,
  DxToolbar,
  DxItem,
  DxStateStoring,
  DxScrolling,
  DxPager,
} from "devextreme-vue/data-grid";
export default {
  name: "trackAndTrace",
  title: "Track and Trace",
  components: {
    PageHeader,
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxColumnChooser,
    DxToolbar,
    DxItem,
    DxDropDownButton,
    DxStateStoring,
    Button,
    DxScrolling,
    DxPager,
  },
  data() {
    return {
      // title : "Track and Trace",
      dataGridRefName,
      allowedOperations: ["contains"],
      selectedOperation: "contains", // default icon
      buttonData: ["Excel", "CSV", "PDF"],
      tracAndTraceColumns: tracAndTraceColumns,
      showClearAll: false,
      usersData: [],
      usersOptions: [],
      showTable: false,
      usersLoading: true,
      isFilter: false,
      backgroundColor: "#f2f2f2",
      user: null,
      date: null,
      rtlEnabled: false,
    };
  },
  methods: {
    ...mapActions("grid", [
      "onTableHeader",
      "updateTableHeaders",
      "createTableHeaders",
    ]),
    ...mapActions("reports", ["fetchTrackTrace"]),
    onToggleVisible() {
      this.$bvModal.show("emergencyListColumns");
    },
    contentReady(e) {
      let sortedExp = this.getSortedColumns();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAll = true;
      } else {
        this.showClearAll = false;
      }
      this.setColumnChooserPosition();
    },
    getSortedColumns() {
      let allColumns = Array.from(
        Array(this.dataGrid.columnCount()).keys()
      ).map((index) => this.dataGrid.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    setColumnChooserPosition() {
      var columnChooserView = this.dataGrid.getView("columnChooserView");
      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }
      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: this.rtlEnabled ? "right top" : "left top",
        at: this.rtlEnabled ? "right top" : "left top",
        offset: "0 50",
      });
    },
    async customLoad() {
      if (this.userEmail != "lt_techteam@visipoint.com") {
        await this.onTableHeader({
          url: "/track-and-trace-list",
        });
        let value = this.getTableHeaders;
        console.log("customLoad");
        return value;
      }
    },
    customSave(gridState) {
      if (this.userEmail != "lt_techteam@visipoint.com") {
        gridState.pageIndex = 0;
        gridState.pageSize = 50;
        let value = JSON.stringify(gridState);
        value = value.replace(',"selectedRowKeys":[]', "");
        if (!this.getTableHeaders) {
          this.createTableHeaders({
            url: "/track-and-trace-list",
            headers: value,
            filters: "[]",
          });
        } else if (value != JSON.stringify(this.getTableHeaders)) {
          this.updateTableHeaders({
            url: "/track-and-trace-list",
            headers: value,
            filters: "[]",
          });
        }
        console.log(
          "customSave values",
          value != JSON.stringify(this.getTableHeaders)
        );
      }
    },
    onEditorPreparing(e) {
      if (e.parentType == "filterRow") {
        e.editorOptions.placeholder = this.$t("common.grid.search");
      }
    },
    onItemClick(e) {
      e.rtlEnabled = this.rtlEnabled;
      if (e.itemData == "Excel") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");
        exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: function (options) {
            const excelCell = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(function () {
          workbook.xlsx.writeBuffer().then(function (buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              "Track-and-Trace-List.xlsx"
            );
          });
        });
        // e.cancel = true;
      } else if (e.itemData == "CSV") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");

        exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: function (options) {
            const excelCell = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(function () {
          workbook.csv.writeBuffer().then(function (buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              "Track-and-Trace-List.csv"
            );
          });
        });
      } else if (e.itemData == "PDF") {
        const doc = new jsPDF('landscape');
        
        // Add custom font
        doc.addFont(amiriFont, "Amiri", "normal");
        doc.setFont("Amiri");

        doc.autoTable({ direction: "rtl" });

        exportDataGridToPdf({
          jsPDFDocument: doc,
          component: this.dataGrid,
          customizeCell: function (options) {
            const { gridCell, pdfCell } = options;

            if (gridCell.rowType === "data") {
              // data columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                cellWidth: "auto",
                halign: "center",
              };
            } else {
              // header columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                fillColor: "#2680EB",
                textColor: "#FFFFFF",
                cellWidth: "auto",
                halign: "center", 
              };
            }
          },
          customizeTable: (doc) => {
            doc.autoTable({ direction: "rtl" }); // Ensure table follows RTL direction
          },
        }).then(() => {
          const pageCount = doc.internal.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const pageSize = doc.internal.pageSize;
            const pageWidth = pageSize.width
              ? pageSize.width
              : pageSize.getWidth();
            const pageHeight = pageSize.height
              ? pageSize.height
              : pageSize.getHeight();
            const header = this.$t('trackAndTrace.title');
            const headerWidth = doc.getTextDimensions(header).w;
            const footer = `${this.$t("common.page")} ${i} ${this.$t("common.of")} ${pageCount}`;

            // Header
            doc.text(header, (pageWidth - headerWidth) / 2, 10);

            // Footer
            doc.text(
              footer,
              pageWidth / 2 - doc.getTextWidth(footer) / 2,
              pageHeight - 5,
              { baseline: "bottom" }
            );
          }

          doc.save("Track and Trace List.pdf");
        });
      }
    },
    reset() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();
      this.showClearAll = false;
    },
    changeDateFilter(value) {
      // console.log('value', value);
      if (value && value[0] == null) this.date = null;
    },
    formattingString(string) {
      console.log("string", string);
      string = string.toString();
      string = string.split(",");
      string = string.join("\n");

      console.log(string);

      return string;
    },
    async onFilterSubmit() {
      this.isFilter = true;
      const { data } = await reportsService.getTrackTrace({
        id: [this.user.id],
        date: this.date,
      });
      this.showTable = true;
      this.isFilter = false;
      this.usersData = data;
    },
    onDataErrorOccurred(e) {
      console.log("onDataErrorOccurred e", e);
      setTimeout(() => {
        let errorRow = document.querySelector(
          ".dx-datagrid-headers .dx-error-message"
        );
        errorRow.innerHTML = this.$t("common.notes.anErrorOccurredGrid");
      }, 100);
    },
  },
  watch: {
    getTableHeaders(value) {
      console.log("watch", value);
    },
    getExportProgessAppearance(value) {
      value && this.$bvModal.show("exportingProgress");
    },
  },
  computed: {
    ...mapGetters("i18N", ["getLanguage"]),
    ...mapGetters("grid", ["getTableHeaders", "getExportProgessAppearance"]),
    ...mapGetters("authfack", ["userDetails", "currentEntity"]),
    rowsData() {
      return this.usersData && this.usersData.length > 0
        ? this.usersData.map((row) => {
            return {
              firstName: row.first_name ? row.first_name : "-",
              lastName: row.last_name ? row.last_name : "-",
              userType: row.current_entity.user_type
                ? row.current_entity.user_type.name
                : "-",
              inputType:
                row.inputTypes && row.inputTypes.length > 0
                  ? this.formattingString(row.inputTypes)
                  : "-",
              device:
                row.devices && row.devices.length > 0
                  ? this.formattingString(row.devices)
                  : "-",
              date:
                row.dates && row.dates.length > 0
                  ? this.formattingString(row.dates)
                  : "-",
              sites:
                row.sites && row.sites.length > 0
                  ? this.formattingString(row.sites)
                  : "-",
              areas:
                row.areas && row.areas.length > 0
                  ? this.formattingString(row.areas)
                  : "-",
            };
          })
        : [];
    },
    dataGrid() {
      return this.$refs[this.dataGridRefName].instance;
    },
    userEmail() {
      return this.userDetails && this.userDetails.email;
    },
  },
  async beforeMount() {
    if (this.getLanguage == "ar") {
      this.rtlEnabled = true;
    }
    this.backgroundColor = "";
    this.backgroundColor = "red";
    try {
      const { data } = await reportsService.getUsers();
      this.usersOptions = data;
    } catch (e) {
      console.error("Could not fetch users", e);
    } finally {
      this.usersLoading = false;
    }
  },
  created() {
    document.addEventListener("mousedown", (e) => {
      if (e.path) {
        let isColumnChooser = false;
        for (let i = 0; i < e.path.length; i++) {
          if (typeof e.path[i].matches == "function") {
            if (e.path[i].matches(".dx-overlay-content")) {
              isColumnChooser = true;
            }
          }
        }
        if (!isColumnChooser) this.dataGrid.hideColumnChooser();
      }
    });
  },
};
</script>

<style lang="scss" scoped>
button.btn.d-flex.align-items-center.justify-content-center.text-center.filter-btn.btn-primary.btn-sm.rounded-pill {
  margin-inset-inline-start: 40px;
  height: 27px;
  width: 127px;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: #ffffff;
}
.filters-wrapper {
  padding: 32px 32px 0 32px;
  .filter-title {
    color: #495057;
    font-weight: 400;
  }
  .filter {
    width: 361px !important;
    height: 55px !important;
  }
}
.search-wrapper {
  width: 20%;
  margin: 1% 0;
}

.emptyspace-ubder-btn {
  height: 23px;
  background: #ffffff;
  display: flex;
  align-items: end;
  justify-content: center;

  .sep-btn-line {
    width: 100%;
    height: 1px;
    background: #000000;
    opacity: 0.08;
  }
}

p {
  font-size: 12px;
  color: #b9b7b7;
}

.emergency-list-table {
  tbody {
    color: #495057;
  }
}
h6.track-trace-hint-grid {
  margin-top: 3rem !important;
  margin-inline-start: 32px;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;

  margin-bottom: 15px !important;
  color: #939393;
}

.filter-users-track-trace {
  margin-inline-start: 100px !important;
}
</style>

<style lang="scss">
.pre-reg-datepicker {
  .mx-input {
    height: 55px !important;
  }
  input.mx-input::placeholder {
    font-weight: 700;
    font-size: 16px;
    line-height: 17px;
    letter-spacing: 0.25px;
    margin-inline-start: 20px;
    padding-inline-start: 10px;
    color: #787878;
  }
  .mx-input:disabled {
    &::placeholder {
      color: #787878;
      opacity: 0.2;
    }
  }
}

div#trackAndTraceGridContainer {
  .dx-datagrid {
    height: calc(100vh - 307px);
  }
  .dx-datagrid-headers .dx-datagrid-table .dx-row > td {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    color: #939393;
  }

  .dx-datagrid-headers .dx-datagrid-content {
    margin-bottom: -5px !important;
  }

  .dx-placeholder {
    margin-top: 3px !important;
  }
}

.whiteSpaceColumn {
  white-space: break-spaces !important;
}

.track-trace-white-bg-sec {
  background-color: white;
  // padding: 32px;
  border-radius: 8px !important;
}

.white-section-height {
  height: 490px;
}
</style>
